import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import HrmsImage from "../../assets/Hrms.png";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { axiosInstance, localAxiosGet, localAxios, localAxiosPut } from '../../Axios/axios';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const EmailVerification = () => {
    const { token } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryToken = queryParams.get('token');

    const actualToken = token || queryToken;

    const navigate = useNavigate();
    const [step, setStep] = useState('verifying'); 
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('Verifying your email...');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!actualToken) {
            setMessage('Invalid verification link');
            setStep('error');
            return;
        }

        const verifyEmailToken = async () => {
            try {
                const response = await axiosInstance(localAxiosGet(`/organization/verifyEmail?token=${actualToken}`));

                if (response.status === 200) {
                    setStep('verified');
                    setMessage('Email verified successfully! Redirecting to OTP page...');

                    try {
                        const tokenParts = actualToken.split('.');
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            if (payload.email) {
                                setEmail(payload.email);

                                setTimeout(() => {
                                    setStep('otp');
                                    requestOTP(payload.email);
                                }, 2000);
                            } else {
                                setStep('email');
                                setMessage('Please enter your email to continue');
                            }
                        }
                    } catch (error) {
                        setStep('email');
                        setMessage('Please enter your email to continue');
                    }
                }
            } catch (error) {
                console.error('Verification error:', error);
                setMessage('Email verification failed. The link may be invalid or expired.');
                setStep('error');
            }
        };

        verifyEmailToken();
    }, [actualToken, navigate]);

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (email) {
            requestOTP(email);
        } else {
            setMessage('Please enter a valid email');
        }
    };

    const requestOTP = async (emailToUse) => {
        setIsSubmitting(true);
        setMessage('Sending OTP...');
        try {
            const response = await axiosInstance(localAxios('/organization/generateOtp', {
                email: emailToUse || email
            }));

            if (response.status === 200) {
                setStep('otp');
                setMessage('OTP has been sent to your email');
            } else {
                setMessage(response.data.message || 'Failed to send OTP');
                setStep('error');
            }
        } catch (error) {
            console.error('Error requesting OTP:', error);
            setMessage('An error occurred while requesting OTP');
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const verifyOTPAndSetPassword = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('Verifying OTP and setting password...');

        if (password !== confirmPassword) {
            setMessage("Passwords don't match!");
            setStep('error');
            setIsSubmitting(false);
            return;
        }

        if (password.length < 6) {
            setMessage("Password must be at least 6 characters long");
            setStep('error');
            setIsSubmitting(false);
            return;
        }

        try {
           
            const verifyResponse = await axiosInstance(localAxios('/organization/verifyOtp', {
                email,
                otp
            }));

            if (verifyResponse.status === 200) {
               
                const updateResponse = await axiosInstance(localAxiosPut('/organization/updatePassword', {
                    email,
                    newPassword: password,
                    confirmPassword
                }));

                if (updateResponse.status === 200) {
                    setStep('success');
                    setMessage('Password set successfully! Redirecting to login...');
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                } else {
                    setMessage(updateResponse.data.message || 'Failed to update password');
                    setStep('error');
                }
            } else {
                setMessage(verifyResponse.data.message || 'Invalid OTP');
                setStep('error');
            }
        } catch (error) {
            console.error('Error during verification:', error);
            setMessage(error.response?.data?.message || 'An error occurred during verification');
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resendOTP = (emailToResend) => {
        requestOTP(emailToResend);
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    

   
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleMouseDownConfirmPassword = (event) => {
        event.preventDefault();
    };

    return (
        <Box
        sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: "100vh",
            width: "100vw",
            backgroundColor: "rgba(41, 41, 41, 1)",
            position: "relative",
        }}
    >
            {isSubmitting && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        zIndex: 1000,
                        backdropFilter: "blur(3px)",
                    }}
                >
                    <CircularProgress color="inherit" />
                </Box>
            )}
           <Box
                sx={{
                    flex: 1,
                    backgroundImage: `url(${HrmsImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                }}
            ></Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: { xs: 3, md: 4 },
                }}
            >
               <Box
                    sx={{
                        width: { xs: "90%", md: "60%" },
                        maxWidth: "350px",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "bold",
                            mb: 2,
                            color: "#ffffff",
                            textAlign: "center",
                        }}
                    >
                        Email Verification
                    </Typography>

                    {message && (
                        <Typography
                            variant="body2"
                            className={
                                step === 'success' ? 'success-message' :
                                step === 'error' ? 'error-message' :
                                'info-message'
                            }
                            sx={{
                                mb: 2,
                                textAlign: "center",
                                color: step === 'success' ? 'green' : step === 'error' ? 'red' : 'yellow',
                                backgroundColor: step === 'success' ? 'rgba(0, 255, 0, 0.1)' : step === 'error' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 255, 0, 0.1)',
                                padding: '8px',
                                borderRadius: '4px',
                            }}
                        >
                            {message}
                        </Typography>
                    )}

                    {step === 'verifying' && (
                        <Box className="verification-step" sx={{ textAlign: 'center', mt: 2 }}>
                            <CircularProgress color="inherit" sx={{ mb: 2 }} />
                            <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                {message}
                            </Typography>
                        </Box>
                    )}

                    {step === 'email' && (
                        <Box className="verification-step">
                            <form onSubmit={handleEmailSubmit}>
                                <TextField
                                    fullWidth
                                    label="Enter your email:"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                    InputProps={{ style: { color: '#000000' } }}
                                    InputLabelProps={{ style: { color: '#bbbbbb' } }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#1a237e",
                                        color: "#fff",
                                        padding: "10px",
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        borderRadius: "4px",
                                        '&:hover': { backgroundColor: '#0d47a1' },
                                        '&:disabled': { backgroundColor: '#344195', color: '#ffffffaa' },
                                        marginTop: '16px',
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Continue
                                </Button>
                            </form>
                        </Box>
                    )}

                    {step === 'otp' && (
                        <Box className="verification-step">
                            <form onSubmit={verifyOTPAndSetPassword}>
                                <TextField
                                    fullWidth
                                    label="Enter OTP:"
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    placeholder="Enter 6-digit OTP"
                                    maxLength="6"
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                    InputProps={{ style: { color: '#000000' } }}
                                    InputLabelProps={{ style: { color: '#bbbbbb' } }}
                                />
                                <TextField
                                    fullWidth
                                    label="New Password:"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                    InputProps={{
                                        style: { color: '#000000' },
                                     
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    InputLabelProps={{ style: { color: '#bbbbbb' } }}
                                   
                                   
                                />
                                <TextField
                                    fullWidth
                                    label="Confirm Password:"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={{ backgroundColor: '#ffffff', borderRadius: '4px' }}
                                    InputProps={{ style: { color: '#000000' },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={handleClickShowConfirmPassword}
                                                onMouseDown={handleMouseDownConfirmPassword}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                    InputLabelProps={{ style: { color: '#bbbbbb' } }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#1a237e",
                                        color: "#fff",
                                        padding: "10px",
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        borderRadius: "4px",
                                        '&:hover': { backgroundColor: '#0d47a1' },
                                        '&:disabled': { backgroundColor: '#344195', color: '#ffffffaa' },
                                        marginTop: '16px',
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Verify & Set Password
                                </Button>
                            </form>
                            <Typography variant="body2" sx={{ mt: 2, color: '#bbbbbb', textAlign: 'center' }}>
                                Didn't receive OTP?
                                <Button onClick={() => resendOTP(email)} color="primary" sx={{ ml: 1, textTransform: 'none' }} disabled={isSubmitting}>
                                    Resend OTP
                                </Button>
                            </Typography>
                        </Box>
                    )}

                    {step === 'error' && (
                        <Box className="verification-step" sx={{ textAlign: 'center', mt: 2 }}>
                            {/* <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>
                                {message} 
                            </Typography> */}
                            <Typography variant="body2" sx={{ color:"#FFF", mb: 2 }}>
                                Please refresh the page and again set the password
                            </Typography>
                            {/* <Button
                                onClick={() => navigate('/login')}
                                variant="contained"
                                sx={{
                                    backgroundColor: "#1a237e",
                                    color: "#fff",
                                    padding: "10px",
                                    fontWeight: "bold",
                                    fontSize: "0.9rem",
                                    borderRadius: "4px",
                                    '&:hover': { backgroundColor: '#0d47a1' },
                                }}
                            >
                                Go to Login
                            </Button> */}
                        </Box>
                    )}

                    {step === 'success' && (
                        <Box className="verification-step" sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="h6" sx={{ color: 'green', mb: 2 }}>
                                ✓
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                {message}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default EmailVerification;